import { Button } from 'antd';
import * as S from './CheckMicrophone.styles';
import { Progress } from '@app/components/common/Progress/Progress.styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CheckMicrophone: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'recording' | 'stopped' | 'playing'>('idle');
  const [progress, setProgress] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (status === 'recording' || status === 'playing') {
      setProgress(0);
      intervalRef.current = setInterval(() => {
        setProgress((prev) => (prev < 100 ? prev + 2 : 100));
      }, 200);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [status]);

  const startRecording = async () => {
    setStatus('recording');
    setAudioURL(null);
    setProgress(0);

    // start beep sound
    const beep = new Audio('/audio/beep.mp3');
    beep.play().catch((err) => console.error('Lỗi phát âm thanh:', err));

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: 'audio/mp3' });
      const url = URL.createObjectURL(audioBlob);
      setAudioURL(url);
      setStatus('stopped');
      setProgress(100);
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
  };

  const stopRecording = () => {
    if (status === 'recording' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    } else if (status === 'playing' && audioRef.current) {
      audioRef.current.pause();
      setStatus('stopped');
      setProgress(100);
    }
  };

  const playRecording = () => {
    if (audioURL) {
      const audio = new Audio(audioURL);
      setStatus('playing');
      setProgress(0);

      audio.play();
      audio.addEventListener('ended', () => {
        setStatus('stopped');
        setProgress(100);
      });
    }
  };

  return (
    <div className="flex flex-col rounded-lg bg-white p-8">
      <div className="font-semibold text-sm">
        <p>{t('page.mockTest.microphoneCheck.title')}</p>
        <p>{t('page.mockTest.microphoneCheck.microphoneCheckDescription')}</p>
        <p>{t('page.mockTest.microphoneCheck.instruction1')}</p>
        <p>{t('page.mockTest.microphoneCheck.instruction2')}</p>
        <p>{t('page.mockTest.microphoneCheck.instruction3')}</p>
        <p>{t('page.mockTest.microphoneCheck.instruction4')}</p>
        <p>{t('page.mockTest.microphoneCheck.instruction5')}</p>
      </div>
      <S.BoxRecorded>
        <S.BoxWrapper>
          <S.BoxTitle>{t('page.mockTest.recordedAnswer')}</S.BoxTitle>
          <S.BoxStatus>
            {t('page.mockTest.currentStatus')}:{status === 'idle' && <p>{t('page.mockTest.clickRecord')}</p>}
            {status === 'recording' && <p>{t('page.mockTest.button.recording')}</p>}
            {status === 'playing' && <p>{t('page.mockTest.button.playing')}</p>}
            {status === 'stopped' && <p>{t('page.mockTest.button.complete')}</p>}
          </S.BoxStatus>
          <S.ProgressBar>
            <Progress
              percent={progress}
              showInfo={false}
              strokeColor="#616161"
              trailColor="#CCCCCC"
              size="small"
              strokeLinecap="butt"
            />
          </S.ProgressBar>
          <S.BtnAction>
            <Button
              type="primary"
              size="small"
              onClick={startRecording}
              disabled={status === 'recording' || status === 'playing'}
            >
              {t('page.mockTest.button.record')}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={playRecording}
              disabled={!audioURL || status === 'recording' || status === 'playing'}
            >
              {t('page.mockTest.button.playBack')}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={stopRecording}
              disabled={status === 'idle' || status === 'stopped'}
            >
              {t('page.mockTest.button.stop')}
            </Button>
          </S.BtnAction>
        </S.BoxWrapper>
      </S.BoxRecorded>

      <div className="font-semibold text-sm">
        <p>{t('page.mockTest.microphoneCheck.note')}</p>
      </div>
    </div>
  );
};
